import React, { FC, useCallback, useContext } from 'react';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { ButtonV2, Flex, JoyKitThemeProvider, LinkV2, LinkV2Props, Spinner, TextV2, useDisclosure } from '@withjoy/joykit';
import { ensureHttpsPrefix } from '@shared/utils/urlHelpers';
import { AnalyticsContext, useTranslation } from '@shared/core';
import { Close, WebGlobe } from '@withjoy/joykit/icons';
import { drawerOverrides } from '@shared/components/ResponsiveMenu/ResponsiveMenu.styles';
import { pxToRem } from '@withjoy/joykit/theme';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { StarRating } from '@shared/components';
import { withWindow } from '@shared/utils/withWindow';
import { useFindHotelMatchLazyQuery } from '@graphql/generated';
import { LinkConfirmationDialog } from '../LinkConfirmationDialog';
import { addError } from '@shared/utils/logger';
import { HOTEL_IMAGE_PLACEHOLDER } from '@shared/utils/constants';

interface HotelSuggestionDialogProps extends LinkV2Props {
  isAllowListedUrl?: boolean;
}

export const HotelSuggestionDialog: FC<HotelSuggestionDialogProps> = ({ href: hrefProp, onClick, children, isAllowListedUrl, ...restProps }) => {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { isOpen: isOpenLinkConfirmationDialog, onClose: onCloseLinkConfirmationDialog, onOpen: onOpenLinkConfirmationDialog } = useDisclosure();

  const analytics = useContext(AnalyticsContext);

  const openHotelSuggestionDialog = useCallback(() => {
    onOpen();
    analytics.page({ action: 'enter', category: 'website', pagePrefix: 'guest', page: 'guestRateAvailableModal', extraPageInfo: { name: 'JoyHotelLink' } });
  }, [onOpen, analytics]);

  const openSafeLinkUrl = useCallback(() => {
    analytics.track({
      action: 'LinkInteracted',
      category: 'safeLink',
      extraInfo: { actionType: 'click', url: hrefProp, isAllowListedUrl }
    });
    withWindow(window => {
      window.open(hrefProp, '_blank');
    });
  }, [analytics, hrefProp, isAllowListedUrl]);

  const [findHotelMatch, { data: hotelMatchData, called, loading: isLoadingHotelMatchData }] = useFindHotelMatchLazyQuery({
    onCompleted: data => {
      if (data.findHotelMatch) {
        openHotelSuggestionDialog();
      } else {
        onClose();
        isAllowListedUrl ? openSafeLinkUrl() : onOpenLinkConfirmationDialog();
      }
    },
    onError: error => {
      addError(error, { href, type: 'findHotelMatch' });
      onClose();
      isAllowListedUrl ? openSafeLinkUrl() : onOpenLinkConfirmationDialog();
    },
    batchMode: 'fast'
  });

  const { t } = useTranslation('joykit');
  const hotelSuggestionDialogTransFn = t('hotelSuggestionDialog');

  const href = ensureHttpsPrefix(hrefProp?.trim() || '');

  const linkProps = {
    textAlign: 'left',
    ...restProps,
    as: 'button',
    onClick: (...args) => {
      if (!href) {
        onClose();
        return;
      }
      onClick?.(...args);
      if (!called) {
        onOpen();
        findHotelMatch({ variables: { url: href } });
      } else if (hotelMatchData?.findHotelMatch) {
        openHotelSuggestionDialog();
      } else {
        isAllowListedUrl ? openSafeLinkUrl() : onOpenLinkConfirmationDialog();
      }
    }
  } as LinkV2Props;

  const onViewHotelClick = useEventCallback(() => {
    analytics.track({ action: 'ButtonInteracted', category: 'guestRateAvailableModal', extraInfo: { name: 'JoyHotelLink', url: hotelMatchData?.findHotelMatch?.link } });
    withWindow(window => {
      window.open(hotelMatchData?.findHotelMatch?.link, 'hotelSuggestionLink');
    });
  });

  const onOpenExternalLinkClick = useEventCallback(() => {
    analytics.track({ action: 'ButtonInteracted', category: 'guestRateAvailableModal', extraInfo: { name: 'ExternalLink', url: href } });
    withWindow(window => {
      window.open(href, 'hotelSuggestionExternalLink');
    });
  });

  return (
    <>
      <LinkV2 {...linkProps}>{children}</LinkV2>
      <JoyKitThemeProvider>
        <ResponsiveDialog
          isOpen={isOpen}
          onClose={onClose}
          hasCloseButton={false}
          removeBodyPadding={true}
          dialogOverrides={{
            Content: { props: { width: '100%', maxWidth: pxToRem(540), paddingX: 9 } },
            Body: { props: { paddingX: 0, paddingY: 9, paddingBottom: 7 } },
            Footer: { props: { paddingX: { _: 6, sm: 6 }, paddingY: 6 } }
          }}
          drawerOverrides={{
            ...drawerOverrides,
            Content: {
              props: {
                ...drawerOverrides.Content?.props,
                paddingY: 0
              }
            },
            Body: {
              props: {
                ...drawerOverrides.Body?.props,
                paddingX: 6,
                paddingTop: 6,
                paddingBottom: 7
              }
            },
            Footer: { props: { paddingX: 6, paddingY: 6 } }
          }}
          renderFooter={
            isLoadingHotelMatchData || !hotelMatchData?.findHotelMatch
              ? undefined
              : () => (
                  <Flex width="100%" flexDirection="column" gap={5}>
                    <ButtonV2 variant="solid" size="lg" shape="rounded" intent="neutral" onClick={onViewHotelClick}>
                      {hotelSuggestionDialogTransFn.viewHotel()}
                    </ButtonV2>
                    <ButtonV2 variant="ghost" size="lg" shape="rounded" intent="neutral" onClick={onOpenExternalLinkClick}>
                      {hotelSuggestionDialogTransFn.openExternalLink()}
                    </ButtonV2>
                  </Flex>
                )
          }
        >
          {isLoadingHotelMatchData || !hotelMatchData?.findHotelMatch ? (
            <Flex marginBottom={5} justifyContent="center">
              <Spinner />
            </Flex>
          ) : (
            <Flex flexDirection="column" gap={6} alignItems="flex-start" paddingX={{ _: 0, sm: 6 }}>
              <Flex position="absolute" top={7} right={7} cursor="pointer" display={{ _: 'none', sm: 'flex' }} _hover={{ color: 'mono12' }} _active={{ color: 'mono13' }}>
                <Close size={24} onClick={onClose} />
              </Flex>
              <Flex flexDirection="column" gap={{ _: 1, sm: 3 }} paddingBottom={5} alignItems={{ _: 'flex-start', sm: 'center' }}>
                <TextV2 typographyVariant={{ _: 'hed4', sm: 'hed6' }} fontWeight={600} color="mono14">
                  {hotelSuggestionDialogTransFn.title()}
                </TextV2>
                <TextV2 typographyVariant="body1" color="mono10" textAlign={{ _: 'left', sm: 'center' }}>
                  {hotelSuggestionDialogTransFn.description()}
                </TextV2>
              </Flex>

              <Flex flexDirection="row" width="100%" paddingX={6} paddingY={5} gap={4} borderRadius={4} border="1px solid" borderColor="mono3" backgroundColor="white">
                <Flex
                  flexShrink={0}
                  width={pxToRem(96)}
                  height="auto"
                  borderRadius={pxToRem(12)}
                  background={`url("${hotelMatchData?.findHotelMatch?.thumbnailUrl || HOTEL_IMAGE_PLACEHOLDER}") lightgray 50%`}
                  backgroundSize="cover"
                  backgroundRepeat="no-repeat"
                />

                <Flex flexDirection="column" gap={3} alignItems="flex-start">
                  <TextV2 typographyVariant="hed2" color="mono14">
                    {hotelMatchData?.findHotelMatch?.name}
                  </TextV2>
                  <Flex flexDirection="column" gap={3} alignItems="flex-start" justifyContent="center">
                    <StarRating rating={hotelMatchData?.findHotelMatch?.rating} />
                    <TextV2 typographyVariant="button3" fontSize={pxToRem(14)} color="mono10">
                      {hotelMatchData?.findHotelMatch?.address.address}
                      {/* TODO: Distance not available in the response */}
                      {/* {hotelSuggestionDialogTransFn.distanceLabel({ distance: hotelData.distance })} */}
                    </TextV2>
                    <Flex
                      flexDirection="column"
                      paddingX={4}
                      paddingY={2}
                      justifyContent="center"
                      alignItems="center"
                      gap={3}
                      borderRadius={3}
                      border="1px solid"
                      borderColor="#4951EF"
                    >
                      <TextV2 typographyVariant="button3" fontSize={pxToRem(14)} color="#4951EF">
                        {hotelSuggestionDialogTransFn.exclusiveRate()}
                      </TextV2>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>

              <Flex width="100%" flexDirection="column" gap={5} alignItems="flex-start">
                <TextV2 typographyVariant="hed2" color="#000">
                  {hotelSuggestionDialogTransFn.externalLink()}
                </TextV2>
                <Flex
                  paddingX={5}
                  paddingY={3}
                  width="100%"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="flex-start"
                  gap={3}
                  borderRadius={3}
                  border="1px solid"
                  borderColor="mono3"
                >
                  <WebGlobe size={24} />
                  <TextV2 typographyVariant="button3" fontSize={pxToRem(14)} color="mono14" textOverflow="ellipsis" whiteSpace="nowrap" overflow="hidden">
                    {hrefProp}
                  </TextV2>
                </Flex>
              </Flex>
            </Flex>
          )}
        </ResponsiveDialog>
      </JoyKitThemeProvider>
      {/* fallback when the hotel match is not found, we will show the link confirmation dialog */}
      <LinkConfirmationDialog href={href} isOpen={isOpenLinkConfirmationDialog} onClose={onCloseLinkConfirmationDialog} />
    </>
  );
};
